<template>
  <div>
    <div class="flex flex-wrap gap-3 align-items-end">
      <div class="flex flex-column">
        <label>Estado</label>
        <Dropdown
          v-model="filtros.Estado"
          :options="estados"
          optionLabel="label"
          optionValue="value"
          placeholder="Seleccione Estado"
          class="w-10rem"
        />
      </div>
      <div class="flex flex-column" v-if="isNotificante">
        <label>Departamental</label>
        <Dropdown
          v-model="filtros.DepartamentalID"
          :options="departamentales"
          optionLabel="Nombre"
          optionValue="DepartamentalID"
          placeholder="Seleccione Departamental"
          class="w-12rem"
        />
      </div>
      <div class="flex justify-content-center align-items-end gap-2">
        <div class="flex flex-column">
          <label>Fecha Creación</label>
          <Calendar
            v-model="filtros.FechaCreacionDesde"
            placeholder="Desde"
            dateFormat="dd-mm-yy"
            class="w-12rem"
            :maxDate="hoy"
          />
        </div>
        <p>-</p>
        <Calendar
          v-model="filtros.FechaCreacionHasta"
          :minDate="filtros.FechaCreacionDesde"
          :maxDate="hoy"
          placeholder="Hasta"
          dateFormat="dd-mm-yy"
          class="w-12rem"
        />
      </div>
      <div class="flex justify-content-center align-items-end gap-2">
        <div class="flex flex-column">
          <label>Fecha Respuesta</label>
          <Calendar
            v-model="filtros.FechaRespuestaDesde"
            :maxDate="hoy"
            placeholder="Desde"
            dateFormat="dd-mm-yy"
            class="w-12rem"
          />
        </div>
        <p>-</p>
        <Calendar
          v-model="filtros.FechaRespuestaHasta"
          :minDate="filtros.FechaRespuestaDesde"
          :maxDate="hoy"
          placeholder="Hasta"
          dateFormat="dd-mm-yy"
          class="w-12rem"
        />
      </div>

      <Button
        label="Filtrar"
        icon="pi pi-filter"
        class="p-button-secondary"
        @click="emitirFiltros"
      />
      <Button
        label="Limpiar"
        icon="pi pi-refresh"
        class="p-button-outlined p-button-secondary"
        @click="resetFiltros"
      />
    </div>
    <div class="flex flex-wrap gap-2 mt-3 align-items-end">
      <Chip
        v-for="(item, clave) in filtros"
        v-if="item && !clave.includes('FechaCreacion') && !clave.includes('FechaRespuesta')"
        :key="clave"
        :label="getFiltroLabel(clave, item)"
        class="mr-2"
        removable
        @remove="quitarFiltro(clave)"
      />
      <!-- Grupo: Fecha de Creación -->
      <div
        v-if="filtros.FechaCreacionDesde || filtros.FechaCreacionHasta"
        class="flex flex-column gap-2 border-1 border-300 p-2 border-round"
      >
        <span class="text-xs font-medium text-500">Fecha de Creación</span>
        <div class="flex gap-2">
          <Chip
            v-if="filtros.FechaCreacionDesde"
            :label="getFiltroLabel('FechaCreacionDesde', filtros.FechaCreacionDesde)"
            removable
            @remove="quitarFiltro('FechaCreacionDesde')"
          />
          <Chip
            v-if="filtros.FechaCreacionHasta"
            :label="getFiltroLabel('FechaCreacionHasta', filtros.FechaCreacionHasta)"
            removable
            @remove="quitarFiltro('FechaCreacionHasta')"
          />
        </div>
      </div>
      <!-- Grupo: Fecha de Respuesta -->
      <div
        v-if="filtros.FechaRespuestaDesde || filtros.FechaRespuestaHasta"
        class="flex flex-column gap-2 border-1 border-300 p-2 border-round"
      >
        <span class="text-xs font-medium text-500">Fecha de Respuesta</span>
        <div class="flex gap-2">
          <Chip
            v-if="filtros.FechaRespuestaDesde"
            :label="getFiltroLabel('FechaRespuestaDesde', filtros.FechaRespuestaDesde)"
            removable
            @remove="quitarFiltro('FechaRespuestaDesde')"
          />
          <Chip
            v-if="filtros.FechaRespuestaHasta"
            :label="getFiltroLabel('FechaRespuestaHasta', filtros.FechaRespuestaHasta)"
            removable
            @remove="quitarFiltro('FechaRespuestaHasta')"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, defineComponent } from "vue";
import { useToast } from "../../composables/useToast";
import { useStoreCompat } from "@/composables/useStoreCompat";
import Chip from "primevue/chip";

export default defineComponent({
  name: "NotificationFilters",
  components: { Chip },
  props: {
    isNotificante: {
      type: Boolean,
      required: true,
      default: false,
    },
  },
  setup(props, { emit }) {
    const store = useStoreCompat();
    const toast = useToast();

    const hoy = ref(new Date());

    const filtros = ref({
      Estado: null,
      DepartamentalID: null,
      FechaCreacionDesde: null,
      FechaCreacionHasta: null,
      FechaRespuestaDesde: null,
      FechaRespuestaHasta: null,
    });

    const departamentales = computed(() => store.state.siis.departamentales);

    const emitirFiltros = () => {
      if (validarFechas()) {
        emit("filtrar", { ...filtros.value });
      } else {
        toast.warning("La fecha 'Hasta' no puede ser anterior a la fecha 'Desde'.");
      }
    };

    const quitarFiltro = (clave) => {
      filtros.value[clave] = null;
      emitirFiltros();
    };

    const getFiltroLabel = (clave, valor) => {
      const nombreMap = {
        Estado: "Estado",
        DepartamentalID: "Departamental",
        FechaCreacionDesde: "desde",
        FechaCreacionHasta: "hasta",
        FechaRespuestaDesde: "desde",
        FechaRespuestaHasta: "hasta",
      };

      if (clave.includes("Fecha")) {
        const fecha = new Date(valor);
        const formateada = fecha.toLocaleDateString("es-AR");
        return `${nombreMap[clave]} ${formateada}`;
      }

      if (clave === "DepartamentalID") {
        const dep = departamentales.value.find((d) => d.DepartamentalID === valor);
        return `${nombreMap[clave]} ${dep?.Nombre || ""}`;
      }

      if (clave === "Estado" && !props.isNotificante && valor === "Enviado") {
        return `${nombreMap[clave]} Pendiente `;
      }

      return `${nombreMap[clave]} ${valor}`;
    };

    const resetFiltros = () => {
      filtros.value = {
        Estado: null,
        DepartamentalID: null,
        FechaCreacionDesde: null,
        FechaCreacionHasta: null,
        FechaRespuestaDesde: null,
        FechaRespuestaHasta: null,
      };
      emitirFiltros();
    };

    const validarFechas = () => {
      const { FechaCreacionDesde, FechaCreacionHasta, FechaRespuestaDesde, FechaRespuestaHasta } =
        filtros.value;

      const esRangoValido = (desde, hasta) =>
        !desde || !hasta || new Date(desde) <= new Date(hasta);

      return (
        esRangoValido(FechaCreacionDesde, FechaCreacionHasta) &&
        esRangoValido(FechaRespuestaDesde, FechaRespuestaHasta)
      );
    };

    const buildEstados = () => {
      return [
        { label: props.isNotificante ? "Enviado" : "Pendiente", value: "Enviado" },
        { label: "Respondido", value: "Respondido" },
        { label: "Finalizado", value: "Finalizado" },
      ];
    };

    const estados = ref(buildEstados());

    return {
      hoy,
      filtros,
      estados,
      departamentales,
      emitirFiltros,
      quitarFiltro,
      getFiltroLabel,
      resetFiltros,
      buildEstados,
    };
  },
});
</script>
